import { ActionableNotification, InlineLoading } from '@carbon/react';
import styles from './chart-status.module.scss';

export interface ChartStatusOverlayProps {
  /** A fetch (or the calc lifecycle) is in flight — dims the chart + shows a spinner. */
  isLoading: boolean;
  /** Fetch error message, or null. Suppressed while loading (a refetch supersedes it). */
  error: string | null;
  /** The resolved result has no rows (empty ≠ error) — shows `emptyMessage`. */
  isEmpty: boolean;
  /** Empty-state copy (e.g. "Requires a calculated evaluation." in package mode). */
  emptyMessage?: string;
  /** The "showing a sample of N" note when a scatter was down-sampled, or null. */
  note?: string | null;
  /** Re-run the series fetch (the error notification's Retry). */
  onRetry: () => void;
}

/**
 * Shared status chrome layered over a Charts & Visuals chart body — the chart-grid
 * analogue of the Executive Summary panel's states (`executive-summary.tsx`): the
 * SciChart surface stays mounted underneath, and this overlays a centered spinner
 * (loading), an error notification + Retry (settled error, suppressed while loading), or
 * an empty message (no rows). The `note` (down-sampled scatter) sits bottom-right and
 * doesn't cover the chart. Absolutely positioned — the host must be `position: relative`.
 *
 * @param {ChartStatusOverlayProps} props - The fetch state, empty/sample copy, and retry handler.
 * @return {JSX.Element | null} The overlay, or null when idle with data.
 */
export function ChartStatusOverlay({
  isLoading,
  error,
  isEmpty,
  emptyMessage = 'No data for the current filters.',
  note,
  onRetry,
}: ChartStatusOverlayProps) {
  return (
    <>
      {isLoading && (
        <div className={styles.center} aria-live="polite">
          <InlineLoading description="Loading chart…" />
        </div>
      )}
      {!isLoading && error && (
        <div className={styles.center}>
          <ActionableNotification
            inline
            lowContrast
            hideCloseButton
            kind="error"
            title="Couldn't load chart"
            subtitle={error}
            actionButtonLabel="Retry"
            onActionButtonClick={onRetry}
          />
        </div>
      )}
      {!isLoading && !error && isEmpty && (
        <div className={styles.center}>
          <p className={styles.empty}>{emptyMessage}</p>
        </div>
      )}
      {!isLoading && !error && note && <p className={styles.note}>{note}</p>}
    </>
  );
}
